import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { getAdoraUser, redirectToLogin } from "@adora/shared/utils/auth";

import DataDogManager from "@adora/shared/utils/datadog";

import App from "./routes/home.tsx";
import "./global.css";
import LoginPage from "./routes/login.tsx";
import UserProvider from "./contexts/user-provider.tsx";
import FluxRedirect from "./routes/flux.tsx";
import Brands from "./routes/brands.tsx";
import Users from "./routes/users.tsx";
import DialogProvider from "@adora/shared/contexts/dialog-provider";
import Batches from "./routes/batches.tsx";
import InternalUsers from "./routes/internal-users.tsx";
import AdAssetGroupsPage from "./components/ad-asset-groups-page/index.tsx";
import AdAssetsPage from "./components/ad-assets-page/index.tsx";
import ToastProvider from "@adora/shared/contexts/toast-provider";
import { FluxThemeProvider } from "flux";
import LDProvider from "./contexts/launchdarkly-provider";

import "@fontsource/poppins";
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/500.css";
import "@fontsource/instrument-serif";

import ExperimentsPage from "./components/experiments-page";
import ExperimentCreativesPage from "./components/experiment-creatives-page/index.tsx";
import ReportSettings from "./routes/report-settings.tsx";
import { TanStackQueryProvider } from "./contexts/tanstack-query-provider.tsx";
DataDogManager.init("internal", false);

const protectedLoader = () => {
  const adoraUser = getAdoraUser();
  if (!adoraUser?.id) {
    return redirectToLogin();
  }
  return null;
};

const basePath = import.meta.env.VITE_BASE_PATH?.replace(/\/+$/, "") || undefined;

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <App />,
      loader: protectedLoader,
      children: [
        {
          path: "/batches",
          element: <Batches />
        },
        {
          path: "/brands",
          element: <Brands />
        },
        {
          path: "/brands/:brandId/users",
          element: <Users />,
          loader: ({ params }) => {
            return params;
          }
        },
        {
          path: "/brands/:brandId/experiments",
          element: <ExperimentsPage />
        },
        {
          path: "/brands/:brandId/experiments/:experimentId/creatives",
          element: <ExperimentCreativesPage />
        },
        {
          path: "/brands/:brandId/ad-asset-groups",
          element: <AdAssetGroupsPage />
        },
        {
          path: "/brands/:brandId/ad-asset-groups/:adAssetGroupId/ad_assets",
          element: <AdAssetsPage />
        },
        {
          path: "/brands/:brandId/reports_settings",
          element: <ReportSettings />
        },
        {
          path: "/users",
          element: <InternalUsers />
        }
      ]
    },
    {
      path: "/flux",
      element: <FluxRedirect />
    },
    {
      path: "login",
      element: <LoginPage />
    }
  ],
  { basename: basePath }
);

ReactDOM.createRoot(document.querySelector("#root")!).render(
  <React.StrictMode>
    <TanStackQueryProvider>
      <UserProvider>
        <LDProvider>
          <FluxThemeProvider>
            <ToastProvider>
              <DialogProvider>
                <RouterProvider router={router} />
              </DialogProvider>
            </ToastProvider>
          </FluxThemeProvider>
        </LDProvider>
      </UserProvider>
    </TanStackQueryProvider>
  </React.StrictMode>
);
